/**
 * PI Lab への改善要望の API クライアント。
 *
 * 画面から出した要望も MCP から出した要望も同じ口（product-api）に入る。**判定は
 * API 側の 1 本**にあるので、ここには上限・必須条件を写さない。弾かれたときは
 * `ApiError.message` をそのまま出す（API が返した文言が、そのまま直し方になる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PI Lab への改善要望を登録・一覧・振り分け・判断・チーム課題へ移動する API クライアント
 * @graph-connects /pilab-requests [calls] 改善要望 API
 */

import type { PilabArea } from '@cortex/core-types';
import { request } from './_request.js';

/** 一覧の状態。API の `status` と同じ。 @graph-connects none */
export type ImprovementStatus =
  | 'pending'
  | 'needs_decision'
  | 'rejected'
  | 'withdrawn'
  | 'moved'
  | 'in_progress'
  | 'shipped';

/** AI の振り分け。 @graph-connects none */
export type ImprovementTriage = 'pending' | 'auto_fix' | 'needs_decision' | 'rejected';

/** 人の判断。 @graph-connects none */
export type ImprovementResolution =
  | 'open'
  | 'accepted'
  | 'declined'
  | 'done'
  | 'withdrawn'
  | 'moved';

/** チーム課題に移した先。API の `movedTo` と同じ。 @graph-connects none */
export interface ImprovementMovedTo {
  kind: 'org_issue';
  boardId: string;
  issueId: string;
}

/**
 * 要望の起点。チーム MTG の投稿から流したときだけ付く。API の `source` と同じ。
 * @graph-connects none
 */
export interface ImprovementRequestSource {
  kind: 'team_meeting_post';
  seriesId: string;
  meetingId: string;
  postId: string;
}

/** 要望 1 件。 @graph-connects none */
export interface ImprovementRequest {
  id: string;
  status: ImprovementStatus;
  area: PilabArea;
  title: string;
  body: string;
  reason: string;
  /** スクショの配信 URL */
  attachments: string[];
  requestedBy: string;
  createdAt: string;
  /** どこから流れてきたか。無ければ直接出した要望 */
  source?: ImprovementRequestSource;
  triage: ImprovementTriage;
  triageNote: string;
  triagedBy: string;
  triagedAt: string | null;
  prUrl: string;
  resolution: ImprovementResolution;
  resolutionNote: string;
  resolvedBy: string;
  resolvedAt: string | null;
  /** チーム課題に移した先（`resolution: 'moved'` のときだけ） */
  movedTo?: ImprovementMovedTo;
}

/** 一覧の絞り込み。空の項目は送らない（空文字で絞って 0 件にしないため）。 @graph-connects none */
export interface ImprovementRequestFilter {
  status?: ImprovementStatus;
  requestedBy?: string;
  /**
   * `true` で判断が付いていないものだけ。
   *
   * **「終わったもの」の定義をここに写さない。** 何が終わりかは API の向こう側
   * （domain の `CLOSED_STATUSES`）が決める。状態名で除外する形にすると、状態が
   * 1 つ増えたときに既定の一覧から黙って消える。
   */
  open?: boolean;
}

/** 登録の入力。**理由は必須**（分類を AI に任せるのに要る）。 @graph-connects none */
export interface ImprovementRequestInput {
  area: PilabArea;
  title: string;
  body: string;
  reason: string;
  /** `uploadAttachment` が返した URL */
  attachments?: string[];
  /** 起点。登録のときだけ送る（書き直しでは API が受け付けない） */
  source?: ImprovementRequestSource;
}

/** 人の判断の入力。 @graph-connects none */
export interface ImprovementResolveInput {
  resolution: ImprovementResolution;
  note: string;
}

/** 絞り込みをクエリ文字列にする。 @graph-connects none */
function toQuery(filter: ImprovementRequestFilter): string {
  const params = new URLSearchParams();
  if (filter.status !== undefined) params.set('status', filter.status);
  if (filter.requestedBy !== undefined && filter.requestedBy !== '') {
    params.set('requestedBy', filter.requestedBy);
  }
  if (filter.open !== undefined) params.set('open', String(filter.open));
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/** 改善要望 API。 @graph-connects none */
export const pilabRequestsApi = {
  /** @graph-connects /pilab-requests [calls] 要望の一覧取得 */
  list: (filter: ImprovementRequestFilter = {}): Promise<{ requests: ImprovementRequest[] }> =>
    request(`/pilab-requests${toQuery(filter)}`),

  /** @graph-connects /pilab-requests [calls] 要望の登録 */
  create: (input: ImprovementRequestInput): Promise<ImprovementRequest> =>
    request('/pilab-requests', { method: 'POST', body: JSON.stringify(input) }),

  /** @graph-connects /pilab-requests/attachments [calls] スクショのアップロード */
  uploadAttachment: (input: {
    contentType: string;
    dataBase64: string;
  }): Promise<{ url: string }> =>
    request('/pilab-requests/attachments', { method: 'POST', body: JSON.stringify(input) }),

  /** @graph-connects /pilab-requests/{id} [calls] 要望の書き直し */
  update: (id: string, input: ImprovementRequestInput): Promise<ImprovementRequest> =>
    request(`/pilab-requests/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  /** @graph-connects /pilab-requests/{id}/resolve [calls] 人の判断の記録 */
  resolve: (id: string, input: ImprovementResolveInput): Promise<ImprovementRequest> =>
    request(`/pilab-requests/${encodeURIComponent(id)}/resolve`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** @graph-connects /pilab-requests/{id}/move-to-issue [calls] チーム課題への移動 */
  moveToIssue: (
    id: string,
    input: { boardId: string; columnId?: string },
  ): Promise<{
    request: ImprovementRequest;
    issue: { boardId: string; issueId: string; title: string };
  }> =>
    request(`/pilab-requests/${encodeURIComponent(id)}/move-to-issue`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** @graph-connects /pilab-requests/guideline [calls] 振り分け基準の取得 */
  guideline: (): Promise<{ guideline: string }> => request('/pilab-requests/guideline'),
};
